/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: Header.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Main header component with navigation, user menu, theme controls,
    and comprehensive application header functionality.

 🔧 Notes:
    - Implements responsive header with mobile menu support
    - Includes user profile dropdown and authentication controls
    - Supports theme switching and language selection
    - Integrates with navigation store for breadcrumbs
    - Provides search functionality and notification center
    - Uses Framer Motion for smooth animations and transitions

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, User, Settings, LogOut, ChevronLeft, Search, Sun, Moon, Globe, Key, Users, Shield, BarChart3, Palette, FileText, Database, Bot } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { useNavigationStore } from '../store/navigation';
import { useI18n } from '../hooks/useI18n';
import { useBranding } from '../hooks/useBranding';
import { api } from '../lib/api';
import { useSidebarState } from '../hooks/useSidebarState';
import { useSidebarContext } from '../contexts/SidebarContext';
import TenantDomainSelector from './TenantDomainSelector';
import GlobalSearchModal from './GlobalSearchModal';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import { SimpleTooltip } from './ui/SimpleTooltip';
import { NginxApplyButton } from './header/NginxApplyButton';
import { AclSyncButton } from './header/AclSyncButton';
import { DashboardControls } from './header/DashboardControls';
import { LicenseStatusIndicator } from './header/LicenseStatus';
import { useAiAssistantStore } from '../store/useAiAssistantStore';

export default function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { themePrefs, updateThemePrefs, setThemeMode } = useThemeStore();
  const { currentPageTitle } = useNavigationStore();
  const { t, tWithNS, i18n, changeLanguage, getCurrentLanguage, getAvailableLanguages, refreshKey } = useI18n();
  const { brandingData } = useBranding();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const { isCollapsed: sidebarCollapsed, toggleCollapse } = useSidebarContext();
  const { toggleChat } = useAiAssistantStore();

  const isAdmin = Boolean(user?.isPrimaryAdmin || (user as any)?.roleProfile?.is_admin || (user as any)?.isAdmin);

  // Dynamic Active System Languages
  const [activeLanguages, setActiveLanguages] = useState<Array<{ code: string; name: string; flag: string }>>([
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ]);

  const loadActiveLanguages = useCallback(async () => {
    try {
      const res = await api.get('/api/languages/active');
      if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setActiveLanguages(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load active languages for Header menu:', err);
    }
  }, []);

  useEffect(() => {
    loadActiveLanguages();
    const handleUpdate = () => loadActiveLanguages();
    window.addEventListener('languages-updated', handleUpdate);
    return () => window.removeEventListener('languages-updated', handleUpdate);
  }, [loadActiveLanguages]);

  // Global search hook
  const {
    isOpen: isSearchOpen,
    openSearch,
    closeSearch,
    searchQuery,
    setSearchQuery,
    results: searchResults,
    isLoading: isSearchLoading,
    handleResultSelect
  } = useGlobalSearch();

  const handleLanguageChange = async (locale: string) => {
    try {
      await changeLanguage(locale);
      setShowLanguageMenu(false);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const toggleTheme = () => {
    const currentMode = themePrefs?.mode || 'system';
    const newMode = currentMode === 'dark' ? 'light' : 'dark';
    setThemeMode(newMode);
  };

  const currentLanguage = getCurrentLanguage();
  const availableLanguages = activeLanguages;
  const currentLangInfo = availableLanguages.find(lang => lang.code === currentLanguage) || { code: currentLanguage, name: currentLanguage, flag: '🌐' };

  // Force re-render when language changes
  useEffect(() => {
    // Language change handled
  }, [refreshKey]);


  // Function to get icon for page title
  const getPageIcon = (pageTitle: string | null) => {
    if (!pageTitle) return null;

    const iconMap: Record<string, any> = {
      'Application Keys': Key,
      'Users': Users,
      'Role Profiles': Shield,
      'Log Profiles': FileText,
      'Dashboard': BarChart3,
      'Appearance': Palette,
      'Settings': Settings,
      'Database': Database
    };

    const IconComponent = iconMap[pageTitle];
    return IconComponent ? <IconComponent className="h-5 w-5 text-primary" /> : null;
  };

  // Use sidebar context to get collapsed state
  const isCollapsed = sidebarCollapsed;

  // Get branding data
  const branding = brandingData?.brandingConfig;
  const isDark = document.documentElement.classList.contains('dark');
  const mobileLogo = isDark && branding?.mobileLogoDark ? branding.mobileLogoDark : branding?.mobileLogo;
  const desktopLogo = isDark && branding?.logoDark ? branding.logoDark : branding?.logo;
  const appName = branding?.appName || 'Ring2All';
  const primaryColor = branding?.primaryColor || '#3B82F6';

  return (
    <header className="fixed top-0 left-0 right-0 bg-card border-b border-border w-full py-3 flex items-center justify-between z-[100] h-20 overflow-visible">
      {/* Left side - Logo and Menu Toggle */}
      <div className="flex items-center px-6">
        {isCollapsed ? (
          // Collapsed state - Logo to the left of menu button
          <>
            {/* Mobile Logo - Left side */}
            <motion.div
              className="h-10 w-10 rounded-lg flex items-center justify-center cursor-pointer overflow-hidden mr-4"
              style={{
                backgroundColor: mobileLogo ? 'transparent' : primaryColor
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const startupDialog = user?.startupDialog || 'dashboard';
                window.location.href = `/${startupDialog}`;
              }}
            >
              {mobileLogo ? (
                <img
                  src={mobileLogo}
                  alt={`${appName} Mobile Logo`}
                  className="h-8 w-8 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent) {
                      parent.style.backgroundColor = primaryColor;
                      const textElement = document.createElement('span');
                      textElement.className = 'text-lg font-bold text-white';
                      textElement.textContent = appName.charAt(0).toUpperCase();
                      parent.appendChild(textElement);
                    }
                  }}
                />
              ) : (
                <span className="text-lg font-bold text-white">
                  {appName.charAt(0).toUpperCase()}
                </span>
              )}
            </motion.div>

            {/* Menu button - Right side */}
            <button
              onClick={toggleCollapse}
              className="p-2 hover:bg-accent rounded-md transition-colors text-muted-foreground"
              title={t('sidebar.toggle', 'Toggle Sidebar')}
            >
              <Menu className="h-5 w-5" />
            </button>
          </>
        ) : (
          // Expanded state - Logo superpuesto sobre ambos niveles del menú
          <>
            {/* Desktop Logo - Posicionado relativo al viewport */}
            <motion.div
              className="fixed top-0 left-0 flex items-center justify-center px-6 py-3 bg-muted/20 rounded-tr-lg cursor-pointer transition-all duration-300 z-50"
              style={{
                width: '350px',
                height: '70px'
              }}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => {
                const startupDialog = user?.startupDialog || 'dashboard';
                window.location.href = `/${startupDialog}`;
              }}
            >
              <div className="flex items-center gap-3">
                {desktopLogo ? (
                  <img
                    src={desktopLogo}
                    alt={`${appName} Logo`}
                    className="h-14 w-auto max-w-[260px] object-contain transition-all duration-300"
                  />
                ) : (
                  <div className="flex items-center gap-3">
                    <div
                      className="h-10 w-10 rounded flex items-center justify-center text-sm font-bold text-white"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {appName.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-lg font-semibold text-foreground">{appName}</span>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Toggle button - Posicionado a la derecha del logo extendido */}
            <button
              onClick={toggleCollapse}
              className="p-2 hover:bg-accent rounded-md transition-colors text-muted-foreground ml-[320px]"
              title={t('sidebar.toggle', 'Toggle Sidebar')}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Page Title - Only show when sidebar is expanded, positioned after the logo area */}
        {!isCollapsed && currentPageTitle && (
          <div className="flex items-center gap-3 ml-6">
            <div className="p-1.5 bg-primary/10 rounded-lg">
              {getPageIcon(currentPageTitle)}
            </div>
            <h1 className="text-xl font-semibold text-foreground">
              {currentPageTitle}
            </h1>
          </div>
        )}

        {/* Tenant & Domain Selector - Positioned after the logo/page title area */}
        <TenantDomainSelector />
      </div>

      <div className="flex items-center gap-4">

        {/* License Status Indicator - Shows warnings if needed */}
        {user?.isPrimaryAdmin && <LicenseStatusIndicator />}

        {/* Nginx SSL Apply Button - Shows when pending changes */}
        <NginxApplyButton />

        {/* ACL Sync Button - Shows when pending changes */}
        <AclSyncButton />

        {/* Dashboard Edit Controls - Shows when editing dashboard */}
        <DashboardControls />

        {/* Search Icon Button */}
        <SimpleTooltip content={t('common:globalSearch.open', 'Abrir búsqueda (Ctrl+K)')} position="bottom">
          <button
            onClick={openSearch}
            className="p-2 hover:bg-accent rounded-md transition-colors"
          >
            <Search className="h-5 w-5 text-muted-foreground" />
          </button>
        </SimpleTooltip>

        {/* AI Assistant Toggle */}
        {user?.chatbotEnabled && (
          <SimpleTooltip content="AI Assistant" position="bottom">
            <button
              onClick={toggleChat}
              className="p-2 hover:bg-accent rounded-md transition-colors"
            >
              <Bot className="h-5 w-5 text-primary" />
            </button>
          </SimpleTooltip>
        )}

        {/* Theme Toggle */}
        <SimpleTooltip content={themePrefs?.mode === 'dark' ? t('common:lightMode', 'Modo claro') : t('common:darkMode', 'Modo oscuro')} position="bottom">
          <button
            onClick={toggleTheme}
            className="p-2 hover:bg-accent rounded-md transition-colors"
          >
            {themePrefs?.mode === 'dark' ? (
              <Sun className="h-5 w-5 text-muted-foreground" />
            ) : (
              <Moon className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
        </SimpleTooltip>

        {/* Language Selector */}
        <div className="relative">
          <button
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              className="flex items-center gap-2 p-2 hover:bg-accent rounded-md transition-colors"
            >
              <span className="text-lg">{currentLangInfo?.flag}</span>
              <span className="text-sm font-medium text-foreground">{currentLangInfo?.code.toUpperCase()}</span>
            </button>

          {showLanguageMenu && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-md shadow-lg z-50">
              {availableLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-accent transition-colors ${lang.code === currentLanguage ? 'bg-accent' : ''
                    }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <div>
                    <div className="font-medium text-foreground">{lang.name}</div>
                    <div className="text-sm text-muted-foreground">{lang.code}</div>
                  </div>
                </button>
              ))}
              
              {isAdmin && (
                <>
                  <div className="border-t border-border my-1" />
                  <button
                    onClick={() => {
                      setShowLanguageMenu(false);
                      navigate('/settings/languages');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-accent text-primary text-xs font-medium transition-colors"
                  >
                    <Settings className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{t('languages:menu.management', '⚙️ Management')}</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>

      </div>

      {/* Overlay for closing menus */}
      {showLanguageMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowLanguageMenu(false);
          }}
        />
      )}

      {/* Global Search Modal */}
      <GlobalSearchModal
        isOpen={isSearchOpen}
        onClose={closeSearch}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        results={searchResults}
        isLoading={isSearchLoading}
        onResultSelect={handleResultSelect}
      />
    </header>
  );
}
